import { AudioPlayer } from '@rhg/components/media/audio-player';
import { useAudio } from '@rhg/hooks/use-audio';
import { useAutoHideControls } from '@rhg/hooks/use-auto-hide-controls';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';
import { useProvidedTheme } from '@rhg/hooks/use-provided-theme';
import commonMessages from '@rhg/lang/messages/common';
import { Box, Dialog, ThemeProvider, Typography } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { MediaEvent } from '@rhg/utils/media';
import {
    KeyboardEventHandler,
    useCallback,
    useEffect,
    useId,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { AudioPlayerContent } from '../audio-player/content';
import { AudioPlayerControls, AudioPlayerVariant } from '../audio-player/types';
import { ToggleControlsArea } from '../toggle-controls-area';
import {
    styleAudioContainer,
    styleAudioPlayer,
    styleBottomContainer,
    styleClickableBackgroundRegion,
    styleContent,
    styleDialogBackground,
    styleDialogBackgroundImage,
    styleDialogBottomActionContainer,
    styleHeightChange,
    stylePlayerContainer,
    styleTransparentDialogBackground,
} from './styles';
import { AudioPlayerDialogProps } from './types';
import { AudioPlayerDialogUpperActions } from './upper-actions';
import { useCompactAudioDialogLayout } from './use-compact-audio-dialog-layout';

export function AudioPlayerDialog({
    audio,
    title,
    description,
    narrator,
    author,
    imageSrc,
    isOpen,
    isCloseButtonDisplayed = true,
    isMinimizeButtonDisplayed = true,
    controls = AudioPlayerControls.Regular,
    supportsPlaybackSpeed,
    autoplay,
    previousProgress,
    trackDialog,
    trackProps,
    upperActionButton,
    supportsAddToPlaylist,
    onAddToPlaylist,
    centerAction,
    onMinimize,
    onProgress,
    onClose,
    onEnd,
    onNextTrack,
    onPreviousTrack,
    onControlClick,
    dialogBottomAction,
    dialogBottomActionHeight,
    areControlsPinned,
}: AudioPlayerDialogProps) {
    const { formatMessage } = useIntl();
    const id = useId();
    // The expanded (full-screen) audio player is always dark, regardless of
    // the user's/app theme (EVE-13087).
    const darkTheme = useProvidedTheme('dark');
    const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false);
    // Drive auto-hide from the low-frequency `isPaused` rather than the ~4Hz
    // `timingState`. Previously the dialog re-rendered — and Safari repainted
    // its full-screen background artwork — on every `timeupdate`. EVE-13080.
    const {
        controls: { isShowingSubtitles, isPaused },
    } = useAudio();
    const {
        areControlsHidden,
        reset: showControls,
        hide: hideControls,
    } = useAutoHideControls({
        isPlaying: !isPaused,
        isSuppressed: isSpeedMenuOpen || !!areControlsPinned,
    });
    const { shouldReduceTopPadding, shouldHideExtras } =
        useCompactAudioDialogLayout(!!dialogBottomAction);

    // Reset the speed menu state when the dialog closes so suppression cannot
    // get stuck (e.g. dialog closed while the menu was open).
    useEffect(() => {
        if (!isOpen) {
            setIsSpeedMenuOpen(false);
        }
    }, [isOpen]);

    const progressTracker = useCallback<MediaTrackerFn>(
        (progress) => {
            onProgress?.(progress);
            if (progress.eventType === MediaEvent.Ended) {
                onEnd?.();
            }
        },
        [onEnd, onProgress]
    );

    const toggleControlsDisplay = useCallback(() => {
        if (areControlsHidden) {
            showControls();
        } else {
            hideControls();
        }
    }, [areControlsHidden, showControls, hideControls]);

    const toggleControlsKeyboardListener = useCallback<
        KeyboardEventHandler<HTMLDivElement>
    >(
        ({ code }) => {
            if (code === 'Tab' || code === 'Enter' || code === 'Space') {
                return;
            }

            // Keyboard interaction reveals controls only (does not toggle).
            showControls();
        },
        [showControls]
    );

    return (
        <ThemeProvider theme={darkTheme}>
            <Dialog
                aria-labelledby={id}
                onClose={onClose}
                open={isOpen}
                trackDialog={trackDialog ?? 'AudioPlayer'}
                {...{ name: trackDialog ?? 'AudioPlayer' }}
                trackProps={trackProps}
                fullScreen
                onKeyDown={toggleControlsKeyboardListener}
            >
                <Box
                    sx={sxAppend(
                        imageSrc && {
                            backgroundImage: `url(${imageSrc})`,
                        },
                        styleDialogBackgroundImage
                    )}
                >
                    <Box
                        sx={sxAppend(
                            styleDialogBackground,
                            areControlsHidden &&
                                styleTransparentDialogBackground
                        )}
                    >
                        <Typography id={id} variant="srOnly">
                            {formatMessage(
                                commonMessages['common.audio_player']
                            )}
                        </Typography>
                        <Box sx={stylePlayerContainer}>
                            <AudioPlayerDialogUpperActions
                                isShown={!areControlsHidden}
                                isMinimizeButtonDisplayed={
                                    isMinimizeButtonDisplayed
                                }
                                isCloseButtonDisplayed={isCloseButtonDisplayed}
                                centerAction={centerAction}
                                onMinimize={onMinimize}
                                onClose={onClose}
                            />
                            {!!audio && isOpen && (
                                <Box
                                    sx={styleAudioContainer(
                                        isShowingSubtitles,
                                        shouldReduceTopPadding
                                    )}
                                >
                                    {(title ||
                                        description ||
                                        !!narrator ||
                                        !!author) && (
                                        <AudioPlayerContent
                                            sx={styleContent}
                                            title={title}
                                            description={description}
                                            narrator={narrator}
                                            author={author}
                                            controls={controls}
                                            areControlsHidden={
                                                areControlsHidden
                                            }
                                            shouldHideExtras={shouldHideExtras}
                                        />
                                    )}
                                    <ToggleControlsArea
                                        actionsCount={1}
                                        areControlsHidden={areControlsHidden}
                                        onToggle={toggleControlsDisplay}
                                        hasDialogBottomAction={
                                            !!dialogBottomAction
                                        }
                                        dialogBottomActionHeight={
                                            dialogBottomActionHeight
                                        }
                                        sx={
                                            areControlsHidden
                                                ? undefined
                                                : styleClickableBackgroundRegion
                                        }
                                    />
                                    <Box
                                        sx={sxAppend(
                                            styleBottomContainer,
                                            areControlsHidden &&
                                                styleHeightChange
                                        )}
                                    >
                                        <AudioPlayer
                                            sx={styleAudioPlayer}
                                            audio={audio}
                                            areControlsHidden={
                                                areControlsHidden
                                            }
                                            controls={controls}
                                            supportsPlaybackSpeed={
                                                supportsPlaybackSpeed
                                            }
                                            variant={
                                                AudioPlayerVariant.Expanded
                                            }
                                            autoplay={autoplay}
                                            previousProgress={previousProgress}
                                            onClose={onClose}
                                            trackFn={progressTracker}
                                            loop={
                                                controls ===
                                                AudioPlayerControls.Soundscape
                                            }
                                            onNextTrack={onNextTrack}
                                            onPreviousTrack={onPreviousTrack}
                                            willTrackProgress
                                            onControlClick={onControlClick}
                                            customAction={upperActionButton}
                                            supportsAddToPlaylist={
                                                supportsAddToPlaylist
                                            }
                                            onAddToPlaylist={onAddToPlaylist}
                                            onPlaybackMenuOpenChange={
                                                setIsSpeedMenuOpen
                                            }
                                        />
                                        {dialogBottomAction &&
                                        !areControlsHidden ? (
                                            <Box
                                                sx={
                                                    styleDialogBottomActionContainer
                                                }
                                            >
                                                {dialogBottomAction}
                                            </Box>
                                        ) : null}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        </ThemeProvider>
    );
}
